import { create } from 'zustand';
import { Client, DataSet, Invoice, MatchStatus, ReconciliationItem, Stats } from '../types';
import { loadLocal, saveLocal, localBackup, localRestore, uploadToDrive, downloadFromDrive, syncFromGstPortal } from '../services/storage';
import { reconcileData, calculateStats } from '../services/matcher';
import { useSettingsStore } from './useSettingsStore';

// Initialize Worker
// Initialize Worker
const reconciliationWorker = new Worker(new URL('../services/recon.worker.ts', import.meta.url), { type: 'module' });

interface DataState {
    clients: Client[];
    activeClient: Client | null;
    activePeriod: string;
    dataStore: Record<string, DataSet>; // Key: clientId_YYYY-MM
    syncStatus: 'idle' | 'syncing' | 'synced' | 'error';

    // Actions
    setClients: (clients: Client[]) => void;
    setActiveClient: (client: Client | null) => void;
    setActivePeriod: (period: string) => void;
    saveClients: (clients: Client[]) => Promise<void>;
    addClient: (client: Client) => void;
    updateClient: (client: Client) => void;
    setBulkClients: (clients: Client[]) => void;

    // Data Operations
    loadDataForPeriod: (clientId: string, period: string) => Promise<void>;
    processDataUpdate: (pr: Invoice[], gstr: Invoice[]) => Promise<void>;
    updateReconciliationItem: (id: string, updates: Partial<ReconciliationItem>) => void;
    bulkUpdateReconciliationItems: (itemIds: string[], action: 'confirm' | 'reject' | 'updateStatus', status?: MatchStatus) => void;

    // Sync Operations
    backupData: (filePath: string) => Promise<void>;
    restoreData: (filePath: string) => Promise<boolean>;
    syncToDrive: () => Promise<void>;
    loadFromDrive: () => Promise<void>;
    syncFromPortal: () => Promise<void>;

    // Selectors
    getCurrentData: () => DataSet | null;
    initializeStore: () => Promise<void>;
}

const INITIAL_CLIENTS: Client[] = [];

export const useDataStore = create<DataState>((set, get) => ({
    clients: INITIAL_CLIENTS,
    activeClient: INITIAL_CLIENTS[0],
    activePeriod: new Date().toISOString().slice(0, 7),
    dataStore: {},
    syncStatus: 'idle',

    saveClients: async (clients: Client[]) => {
        const { settings } = useSettingsStore.getState();
        await saveLocal('app_clients', { clients } as any, settings);
    },

    setClients: (clients) => {
        set({ clients });
        get().saveClients(clients);
    },
    setActiveClient: (client) => {
        set({ activeClient: client });
        if (client) localStorage.setItem('last_active_client_id', client.id);
    },
    setActivePeriod: (period) => {
        set({ activePeriod: period });
        localStorage.setItem('last_active_period', period);
    },
    addClient: (client) => {
        const newClients = [...get().clients, client];
        set({ clients: newClients });
        get().saveClients(newClients);
    },
    updateClient: (updatedClient) => {
        const newClients = get().clients.map(c => c.id === updatedClient.id ? updatedClient : c);
        set({
            clients: newClients,
            activeClient: get().activeClient?.id === updatedClient.id ? updatedClient : get().activeClient
        });
        get().saveClients(newClients);
    },
    setBulkClients: (newClients) => {
        const existing = get().clients;
        // Merge without duplicates based on GSTIN
        const existingGstins = new Set(existing.map(c => c.gstin));
        const uniqueNew = newClients.filter(c => !existingGstins.has(c.gstin));
        const combined = [...existing, ...uniqueNew];
        set({ clients: combined });
        get().saveClients(combined);
    },

    initializeStore: async () => {
        const { settings } = useSettingsStore.getState();
        console.log("[DataStore] Initializing... Settings:", settings);
        try {
            const saved = await loadLocal('app_clients', settings);
            console.log("[DataStore] loadLocal('app_clients') result:", saved);
            if (saved && (saved as any).clients) {
                const loadedClients = (saved as any).clients;
                if (loadedClients.length > 0) {
                    set({ clients: loadedClients });
                    console.log("[DataStore] Restored clients:", loadedClients.length);

                    // Restore last active client/period
                    const lastClientId = localStorage.getItem('last_active_client_id');
                    const lastPeriod = localStorage.getItem('last_active_period');

                    if (lastClientId) {
                        const found = loadedClients.find((c: Client) => c.id === lastClientId);
                        if (found) {
                            set({ activeClient: found });
                            console.log("[DataStore] Restored active client:", found.name);
                        }
                    } else if (loadedClients.length > 0) {
                        set({ activeClient: loadedClients[0] });
                    }

                    if (lastPeriod) {
                        set({ activePeriod: lastPeriod });
                        console.log("[DataStore] Restored active period:", lastPeriod);
                    }
                }
            } else {
                console.log("[DataStore] No clients found. Using default.");
                const defaultClient: Client = { id: 'c1', name: 'Alpha Traders Ltd', gstin: '27AAAAA1234A1Z5' };
                set({ clients: [defaultClient], activeClient: defaultClient });
            }
        } catch (err) {
            console.error("Failed to initialize data store:", err);
        }
    },

    getCurrentData: () => {
        const { activeClient, activePeriod, dataStore } = get();
        if (!activeClient || !activePeriod) return null;
        const key = `${activeClient.id}_${activePeriod}`;
        return dataStore[key] || null;
    },

    loadDataForPeriod: async (clientId, period) => {
        const key = `${clientId}_${period}`;
        const { dataStore } = get();
        if (dataStore[key]) return; // Already loaded

        const { settings, encryptionKey } = useSettingsStore.getState();
        try {
            const saved = await loadLocal(key, settings, encryptionKey);
            if (saved) {
                set((state) => ({
                    dataStore: { ...state.dataStore, [key]: saved }
                }));
            }
        } catch (err) {
            console.error(`Failed to load data for ${key}`, err);
        }
    },

    processDataUpdate: async (pr, gstr) => {
        const { activeClient, activePeriod, dataStore } = get();
        if (!activeClient || !activePeriod) return;

        const key = `${activeClient.id}_${activePeriod}`;
        const { settings, encryptionKey } = useSettingsStore.getState();

        // Logic for Previous Period Carry Forward
        const getPreviousPeriodKey = (client: Client, currentPeriod: string): string => {
            if (!currentPeriod.includes('-')) return '';
            const [year, month] = currentPeriod.split('-').map(Number);
            const date = new Date(year, month - 2, 1);
            return `${client.id}_${date.toISOString().slice(0, 7)}`;
        };

        const prevKey = getPreviousPeriodKey(activeClient, activePeriod);
        let workingPrData = [...pr];
        let workingGstrData = [...gstr];

        try {
            // Try to load previous data if not in store
            let prevData = dataStore[prevKey];
            if (!prevData) {
                const loaded = await loadLocal(prevKey, settings, encryptionKey);
                if (loaded) prevData = loaded;
            }

            if (prevData) {
                const currentPrIds = new Set(workingPrData.map(i => i.id));
                const currentGstrIds = new Set(workingGstrData.map(i => i.id));

                prevData.results.forEach(r => {
                    if ((r.status === 'Unmatched PR' || r.status === 'Carry Forward') && r.prInvoiceRef) {
                        if (!currentPrIds.has(r.prInvoiceRef.id)) {
                            workingPrData.push({ ...r.prInvoiceRef, isCarryForward: true });
                        }
                    }
                    if (r.status === 'Unmatched 2B' && r.gstrInvoiceRef) {
                        if (!currentGstrIds.has(r.gstrInvoiceRef.id)) {
                            const isRcm = r.gstrInvoiceRef.rcm === 'Yes';
                            const isIneligible = r.gstrInvoiceRef.itcAvailability === 'No';
                            if (!isRcm && !isIneligible) {
                                workingGstrData.push({ ...r.gstrInvoiceRef, isCarryForward: true });
                            }
                        }
                    }
                });
            }
        } catch (e) {
            console.warn("Error processing previous period data:", e);
        }

        // Use Web Worker for heavy reconciliation
        reconciliationWorker.postMessage({
            type: 'RECONCILE',
            payload: {
                prList: workingPrData,
                gstrList: workingGstrData,
                settings,
                activePeriod,
                clientGstin: activeClient.gstin
            }
        });

        reconciliationWorker.onmessage = (e) => {
            if (e.data.type === 'RECONCILE_SUCCESS') {
                const { results, stats } = e.data.payload;

                // Map status and remarks back to invoices
                const statusMap = new Map<string, string>();
                const matchedMonthMap = new Map<string, string>();
                const remarksMap = new Map<string, string>();

                results.forEach((r: any) => {
                    const updateMaps = (sub: Invoice) => {
                        statusMap.set(sub.id, r.status);
                        if (r.matchedMonth) matchedMonthMap.set(sub.id, r.matchedMonth);

                        // Combine remarks and mismatch details for better visibility in raw data
                        let fullRemark = r.remarks || '';
                        if (r.status === MatchStatus.PARTIALLY_MATCHED && r.mismatchDetails) {
                            fullRemark = fullRemark ? `${fullRemark}: ${r.mismatchDetails}` : r.mismatchDetails;
                        }
                        if (fullRemark) remarksMap.set(sub.id, fullRemark);
                    };

                    if (r.prMatches && r.prMatches.length > 0) r.prMatches.forEach(updateMaps);
                    else if (r.prInvoiceRef) updateMaps(r.prInvoiceRef);

                    if (r.gstrMatches && r.gstrMatches.length > 0) r.gstrMatches.forEach(updateMaps);
                    else if (r.gstrInvoiceRef) updateMaps(r.gstrInvoiceRef);
                });



                const enrichedPR = workingPrData.map(i => ({
                    ...i,
                    reconStatus: statusMap.get(i.id) || i.reconStatus || 'Unreconciled',
                    matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth,
                    remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || '')
                }));

                const enrichedGSTR = workingGstrData.map(i => ({
                    ...i,
                    reconStatus: statusMap.get(i.id) || i.reconStatus || 'Unreconciled',
                    matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth,
                    remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || '')
                }));


                const newData: DataSet = {
                    prData: enrichedPR,
                    gstrData: enrichedGSTR,
                    results,
                    stats,
                    settings: settings,
                    lastUpdated: new Date().toISOString()
                };

                set((state) => ({
                    dataStore: { ...state.dataStore, [key]: newData },
                    syncStatus: 'idle'
                }));

                saveLocal(key, newData, settings, encryptionKey);
            } else if (e.data.type === 'RECONCILE_ERROR') {
                console.error("Reconciliation Worker Error:", e.data.payload);
                set({ syncStatus: 'error' });
            }
        };

        set({ syncStatus: 'syncing' });
    },

    updateReconciliationItem: (id, updates) => {
        const { activeClient, activePeriod, dataStore } = get();
        if (!activeClient || !activePeriod) return;
        const key = `${activeClient.id}_${activePeriod}`;
        const currentData = dataStore[key];
        if (!currentData) return;

        const newResults = currentData.results.map(item => item.id === id ? { ...item, ...updates } : item);
        const newStats = calculateStats(newResults);

        // Map updates back to source data (PR/GSTR)
        const updatedItem = newResults.find(r => r.id === id);
        if (updatedItem) {
            const statusMap = new Map<string, string>();
            const remarksMap = new Map<string, string>();
            const matchedMonthMap = new Map<string, string>();

            const updateSubMaps = (sub: Invoice) => {
                statusMap.set(sub.id, updatedItem.status);
                if (updatedItem.remarks) remarksMap.set(sub.id, updatedItem.remarks);
                else remarksMap.set(sub.id, ''); // Allow clearing remarks

                if (updatedItem.matchedMonth) matchedMonthMap.set(sub.id, updatedItem.matchedMonth);
            };

            if (updatedItem.prMatches && updatedItem.prMatches.length > 0) updatedItem.prMatches.forEach(updateSubMaps);
            else if (updatedItem.prInvoiceRef) updateSubMaps(updatedItem.prInvoiceRef);

            if (updatedItem.gstrMatches && updatedItem.gstrMatches.length > 0) updatedItem.gstrMatches.forEach(updateSubMaps);
            else if (updatedItem.gstrInvoiceRef) updateSubMaps(updatedItem.gstrInvoiceRef);

            const enrichedPR = currentData.prData.map(i => statusMap.has(i.id) ? {
                ...i,
                reconStatus: statusMap.get(i.id),
                remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || ''),
                matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth
            } : i);

            const enrichedGSTR = currentData.gstrData.map(i => statusMap.has(i.id) ? {
                ...i,
                reconStatus: statusMap.get(i.id),
                remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || ''),
                matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth
            } : i);

            const newData = { ...currentData, results: newResults, stats: newStats, prData: enrichedPR, gstrData: enrichedGSTR, lastUpdated: new Date().toISOString() };
            set((state) => ({ dataStore: { ...state.dataStore, [key]: newData } }));
            const { settings, encryptionKey } = useSettingsStore.getState();
            saveLocal(key, newData, settings, encryptionKey);
        }
    },

    bulkUpdateReconciliationItems: (itemIds: string[], action: 'confirm' | 'reject' | 'updateStatus', status?: MatchStatus) => {
        const { activeClient, activePeriod, dataStore } = get();
        if (!activeClient || !activePeriod) return;
        const key = `${activeClient.id}_${activePeriod}`;
        const currentData = dataStore[key];
        if (!currentData) return;

        let newResults = [...currentData.results];

        if (action === 'updateStatus' && status) {
            newResults = newResults.map(item => {
                if (itemIds.includes(item.id)) {
                    return {
                        ...item,
                        status: status,
                        matchedMonth: activePeriod,
                        remarks: `Manual Status Change: ${status}`,
                        mismatchDetails: ''
                    };
                }
                return item;
            });
        } else if (action === 'confirm') {
            newResults = newResults.map(item => {
                if (itemIds.includes(item.id)) {
                    return {
                        ...item,
                        status: MatchStatus.MATCHED,
                        matchedMonth: activePeriod,
                        remarks: item.remarks.includes('Manually Confirmed') ? item.remarks : item.remarks + ' (Manually Confirmed)'
                    };
                }
                return item;
            });
        } else if (action === 'reject') {
            const itemsToReject = newResults.filter(i => itemIds.includes(i.id));
            newResults = newResults.filter(i => !itemIds.includes(i.id));

            // Re-create original items from refs
            itemsToReject.forEach(item => {
                if (item.prInvoiceRef) {
                    newResults.push({
                        id: item.prInvoiceRef.id + '_un',
                        status: MatchStatus.UNMATCHED_PR,
                        remarks: 'Manually Rejected',
                        mismatchDetails: '',
                        diffTax: 0,
                        prInvoiceRef: item.prInvoiceRef,
                        prGstin: item.prGstin, prPartyName: item.prPartyName, prInvoiceNo: item.prInvoiceNo, prDate: item.prDate,
                        prIgst: item.prIgst, prCgst: item.prCgst, prSgst: item.prSgst,
                        gstrIgst: 0, gstrCgst: 0, gstrSgst: 0
                    });
                }
                if (item.gstrInvoiceRef) {
                    newResults.push({
                        id: item.gstrInvoiceRef.id + '_un',
                        status: MatchStatus.UNMATCHED_2B,
                        remarks: 'Manually Rejected',
                        mismatchDetails: '',
                        diffTax: 0,
                        gstrInvoiceRef: item.gstrInvoiceRef,
                        gstrGstin: item.gstrGstin, gstrPartyName: item.gstrPartyName, gstrInvoiceNo: item.gstrInvoiceNo, gstrDate: item.gstrDate,
                        gstrIgst: item.gstrIgst, gstrCgst: item.gstrCgst, gstrSgst: item.gstrSgst,
                        prIgst: 0, prCgst: 0, prSgst: 0
                    });
                }
            });
        }

        const newStats = calculateStats(newResults);

        // Map back to source data for reconStatus and remarks
        const statusMap = new Map<string, string>();
        const matchedMonthMap = new Map<string, string>();
        const remarksMap = new Map<string, string>();

        newResults.forEach(r => {
            const updateMaps = (sub: Invoice) => {
                statusMap.set(sub.id, r.status);
                if (r.matchedMonth) matchedMonthMap.set(sub.id, r.matchedMonth);
                if (r.remarks) remarksMap.set(sub.id, r.remarks);
                else remarksMap.set(sub.id, '');
            };
            if (r.prMatches && r.prMatches.length > 0) r.prMatches.forEach(updateMaps); else if (r.prInvoiceRef) updateMaps(r.prInvoiceRef);
            if (r.gstrMatches && r.gstrMatches.length > 0) r.gstrMatches.forEach(updateMaps); else if (r.gstrInvoiceRef) updateMaps(r.gstrInvoiceRef);
        });

        const enrichedPR = currentData.prData.map(i => ({
            ...i,
            reconStatus: statusMap.get(i.id) || i.reconStatus || 'Unreconciled',
            matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth,
            remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || '')
        }));

        const enrichedGSTR = currentData.gstrData.map(i => ({
            ...i,
            reconStatus: statusMap.get(i.id) || i.reconStatus || 'Unreconciled',
            matchedMonth: matchedMonthMap.get(i.id) || i.matchedMonth,
            remarks: remarksMap.has(i.id) ? (remarksMap.get(i.id) || '') : (i.remarks || '')
        }));

        const newData = { ...currentData, results: newResults, stats: newStats, prData: enrichedPR, gstrData: enrichedGSTR, lastUpdated: new Date().toISOString() };

        set((state) => ({ dataStore: { ...state.dataStore, [key]: newData } }));
        const { settings: currentSettings, encryptionKey: keyEnc } = useSettingsStore.getState();
        saveLocal(key, newData, currentSettings, keyEnc);
    },

    backupData: async (filePath) => {
        const currentData = get().getCurrentData();
        if (!currentData) return;
        await localBackup(filePath, currentData);
    },

    restoreData: async (filePath) => {
        try {
            const restoredData = await localRestore(filePath);
            if (restoredData && get().clients.length > 0) { // Simplified
                // Logic to determine client/period from filename needed in real app
                return true;
            }
            return false;
        } catch (e) { return false; }
    },

    syncToDrive: async () => {
        const { activeClient, activePeriod, getCurrentData } = get();
        const currentData = getCurrentData();
        if (!currentData || !activeClient) return;

        set({ syncStatus: 'syncing' });
        const key = `${activeClient.id}_${activePeriod}`;
        await uploadToDrive(`Recon_${key}.json`, currentData);
        set({ syncStatus: 'synced' });
        setTimeout(() => set({ syncStatus: 'idle' }), 3000);
    },

    loadFromDrive: async () => {
        set({ syncStatus: 'syncing' });
        // Mock
        setTimeout(() => set({ syncStatus: 'idle' }), 1000);
    },

    syncFromPortal: async () => {
        const { activeClient, activePeriod } = get();
        if (!activeClient) return;
        try {
            await syncFromGstPortal(activeClient.gstin, activePeriod);
        } catch (e) {
            console.error(e);
        }
    }
}));
